import React from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import { FeatureCollection } from 'geojson';
import L from 'leaflet';
import { CidadeData } from '../../data/mockData';
import 'leaflet/dist/leaflet.css';

interface MapComponentProps {
  boundaries?: FeatureCollection | null;
  selectedCities?: CidadeData[];
  center?: [number, number];
  zoom?: number;
}

const cityColors: { [key: string]: string } = {
  'sao-paulo': '#3b82f6',
  'santos': '#10b981',
  'campinas': '#f59e0b',
  'ribeirao-preto': '#ef4444',
  'sao-jose-campos': '#8b5cf6',
};

export const MapComponent: React.FC<MapComponentProps> = ({
  boundaries,
  selectedCities = [],
  center,
  zoom = 7
}) => {
  const defaultCenter: [number, number] = [-19.5, -47.5];
  const mapCenter = center || defaultCenter;

  const getFeatureStyle = (feature: any) => {
    const cityName = feature?.properties?.city ||
                     feature?.properties?.name ||
                     feature?.properties?.NAME ||
                     feature?.properties?.nome;

    const city = selectedCities.find(c =>
      c.nome.toLowerCase() === cityName?.toLowerCase()
    );

    if (city) {
      const color = cityColors[city.id] || '#3388ff';
      return {
        color: color,
        fillColor: color,
        fillOpacity: 0.4,
        weight: 2,
        opacity: 1
      };
    }

    return {
      color: '#3388ff',
      fillColor: '#3388ff',
      fillOpacity: 0.2,
      weight: 2,
      opacity: 0.8
    };
  };

  const onEachFeature = (feature: any, layer: L.Layer) => {
    const cityName = feature?.properties?.city ||
                     feature?.properties?.name ||
                     feature?.properties?.NAME ||
                     feature?.properties?.nome;

    const city = selectedCities.find(c =>
      c.nome.toLowerCase() === cityName?.toLowerCase()
    );

    layer.on({
      mouseover: (e: L.LeafletMouseEvent) => {
        const target = e.target as L.Path;
        target.setStyle({
          fillOpacity: 0.7,
          weight: 3
        });
      },
      mouseout: (e: L.LeafletMouseEvent) => {
        const target = e.target as L.Path;
        const style = getFeatureStyle(feature);
        target.setStyle({
          fillOpacity: style.fillOpacity,
          weight: 2
        });
      }
    });

    if (city) {
      const distribuidoresText = city.distribuidores.length > 0
        ? `Distribuidores: ${city.distribuidores.length}`
        : 'Sem distribuidores';

      const popupContent = `
        <div class="text-center p-2">
          <h3 class="font-bold text-lg text-gray-800">${city.nome}</h3>
          <p class="text-sm text-gray-600 mt-1">Estado: ${city.estado}</p>
          <p class="text-xs text-gray-500 mt-1">${distribuidoresText}</p>
        </div>
      `;
      layer.bindPopup(popupContent);
    } else if (cityName) {
      layer.bindPopup(`
        <div class="text-center p-2">
          <h3 class="font-bold text-lg text-gray-800">${cityName}</h3>
        </div>
      `);
    }
  };

  return (
    <div className="h-full w-full rounded-lg overflow-hidden relative">
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        className="rounded-lg"
      >
        <TileLayer
          attribution='Sistema Interno'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />

        {boundaries && (
          <GeoJSON
            key={JSON.stringify(boundaries) + JSON.stringify(selectedCities.map(c => c.id))}
            data={boundaries}
            style={getFeatureStyle}
            onEachFeature={onEachFeature}
          />
        )}
      </MapContainer>

      {!boundaries && (
        <div className="absolute inset-0 bg-gray-100 bg-opacity-90 flex items-center justify-center z-[1000]">
          <div className="text-center">
            <p className="text-gray-700 font-medium text-lg">Selecione os filtros</p>
            <p className="text-gray-500 text-sm mt-2">Use os filtros acima para visualizar as cidades no mapa</p>
          </div>
        </div>
      )}
    </div>
  );
};
